/**
 * Offer Modal
 * Shows product added confirmation and recommendations
 * Products are stored in localStorage via addToOfferteCart
 */
class OfferModal extends HTMLElement {
  constructor() {
    super();
    console.log('OfferModal: Constructor called');

    this.modal = this;
    this.overlay = this.querySelector('.fixed-overlay');
    this.closeButtons = this.querySelectorAll('[aria-controls="OfferModal"]');

    console.log('OfferModal: Elements found:', {
      overlay: !!this.overlay
    });

    this.init();
  }

  init() {
    console.log('OfferModal: Init called');

    // Bind event listeners
    this.bindEvents();

    // Listen for offer button clicks
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-offer-trigger]');
      if (trigger) {
        console.log('OfferModal: Opening with product', trigger.dataset);
        e.preventDefault();
        this.openWithProduct(trigger);
      }
    });

    console.log('OfferModal: Click listener added to document');
  }

  bindEvents() {
    // Close button handlers
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    // Overlay click
    this.overlay?.addEventListener('click', () => this.close());

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.hasAttribute('open')) {
        this.close();
      }
    });
  }

  openWithProduct(trigger) {
    console.log('OfferModal: openWithProduct called');

    const productData = {
      id: trigger.dataset.productId,
      title: trigger.dataset.productTitle,
      price: trigger.dataset.productPrice,
      image: trigger.dataset.productImage,
      handle: trigger.dataset.productHandle,
      variantId: trigger.dataset.variantId,
      perPersoon: trigger.dataset.perPersoon === 'true'
    };

    console.log('OfferModal: Product data:', productData);

    // Add to localStorage cart
    if (typeof window.addToOfferteCart === 'function') {
      window.addToOfferteCart(productData);
    } else {
      // Fallback: add directly to localStorage
      this.addToLocalStorage(productData);
    }

    this.populateProductData(productData);
    console.log('OfferModal: About to call open()');
    this.open();

    // Fetch recommendations
    if (productData.handle) {
      this.fetchRecommendations(productData.handle);
    }
  }

  addToLocalStorage(productData) {
    const storageKey = 'offerte_products';
    let products = [];

    try {
      const data = localStorage.getItem(storageKey);
      products = data ? JSON.parse(data) : [];
    } catch (e) {
      products = [];
    }

    const exists = products.find(p => p.id === productData.id);
    if (!exists) {
      products.push(productData);
      localStorage.setItem(storageKey, JSON.stringify(products));
    }

    // Update header count
    this.updateHeaderCount(products.length);
  }

  updateHeaderCount(count) {
    const countElements = document.querySelectorAll('[data-offerte-count]');
    countElements.forEach(el => {
      el.textContent = count;
      if (count > 0) {
        el.classList.remove('hidden');
      } else {
        el.classList.add('hidden');
      }
    });
  }

  async fetchRecommendations(productHandle) {
    console.log('OfferModal: Fetching recommendations for:', productHandle);

    const container = this.querySelector('[data-recommendations-container]');
    if (!container) {
      console.error('OfferModal: Recommendations container not found');
      return;
    }

    // Show loading state
    container.innerHTML = `
      <div class="offer-modal__recommendations-loading">
        <div class="loading__spinner">
          <svg aria-hidden="true" focusable="false" class="spinner icon icon--medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>
          </svg>
        </div>
      </div>
    `;

    try {
      const url = `/products/${productHandle}?section_id=offer-recommendations`;
      console.log('OfferModal: Fetching URL:', url);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      console.log('OfferModal: Received recommendations HTML');

      // Parse and insert the recommendations
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const recommendationsContent = doc.querySelector('.offer-recommendations');

      if (recommendationsContent) {
        container.innerHTML = recommendationsContent.innerHTML;
        console.log('OfferModal: Recommendations inserted');

        // Re-bind add recommendation buttons
        this.bindRecommendationButtons();
      } else {
        container.innerHTML = '<p class="offer-recommendations__empty">Geen gerelateerde producten gevonden.</p>';
        console.log('OfferModal: No recommendations found in response');
      }
    } catch (error) {
      console.error('OfferModal: Error fetching recommendations:', error);
      container.innerHTML = '<p class="offer-recommendations__empty">Kon aanbevelingen niet laden.</p>';
    }
  }

  bindRecommendationButtons() {
    const addButtons = this.querySelectorAll('[data-add-recommendation]');
    console.log('OfferModal: Binding', addButtons.length, 'recommendation buttons');

    addButtons.forEach(btn => {
      // Remove existing listeners by cloning
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);

      newBtn.addEventListener('click', (e) => this.addRecommendation(e));
    });
  }

  populateProductData(product) {
    // Update confirmation section
    const productImage = this.querySelector('[data-product-image]');
    const productTitle = this.querySelector('[data-product-title]');

    if (productImage) productImage.src = product.image;
    if (productTitle) productTitle.textContent = product.title;
  }

  addRecommendation(e) {
    const btn = e.currentTarget;
    const productData = {
      id: btn.dataset.productId,
      title: btn.dataset.productTitle,
      price: btn.dataset.productPrice,
      image: btn.dataset.productImage,
      handle: btn.dataset.productHandle,
      variantId: btn.dataset.variantId,
      perPersoon: btn.dataset.perPersoon === 'true'
    };

    // Add to localStorage
    if (typeof window.addToOfferteCart === 'function') {
      window.addToOfferteCart(productData);
    } else {
      this.addToLocalStorage(productData);
    }

    // Visual feedback
    btn.classList.add('added');
    btn.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M13.3334 4L6.00002 11.3333L2.66669 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }

  open() {
    console.log('OfferModal: open() called');

    this.modal.setAttribute('open', '');
    this.modal.setAttribute('active', '');
    document.body.classList.add('overflow-hidden');

    console.log('OfferModal: Attributes set - open and active');

    // Trap focus
    this.trapFocus();
  }

  close() {
    console.log('OfferModal: close() called');
    this.modal.removeAttribute('open');
    this.modal.removeAttribute('active');
    document.body.classList.remove('overflow-hidden');
  }

  trapFocus() {
    const focusableElements = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    this.modal.addEventListener('keydown', handleTabKey);

    // Focus first element
    if (firstElement) {
      firstElement.focus();
    }
  }

  formatMoney(cents) {
    const amount = (parseInt(cents) / 100).toFixed(2);
    return `€${amount.replace('.', ',')}`;
  }

  formatPrice(cents, perPersoon = false) {
    const price = parseInt(cents);

    if (price === 0) {
      return 'Prijs op aanvraag';
    }

    const formattedMoney = this.formatMoney(cents);

    if (perPersoon) {
      return `Vanaf ${formattedMoney} p.p.`;
    }

    return `Prijs vanaf: ${formattedMoney}`;
  }
}

customElements.define('offer-modal', OfferModal);

console.log('OfferModal: Custom element defined');

// Initialize header count on page load
document.addEventListener('DOMContentLoaded', () => {
  const count = window.getOfferteCartCount ? window.getOfferteCartCount() : 0;
  const countElements = document.querySelectorAll('[data-offerte-count]');
  countElements.forEach(el => {
    el.textContent = count;
    if (count > 0) {
      el.classList.remove('hidden');
    } else {
      el.classList.add('hidden');
    }
  });
});

// Fallback getOfferteCartCount if not defined
if (typeof window.getOfferteCartCount !== 'function') {
  window.getOfferteCartCount = function() {
    try {
      const data = localStorage.getItem('offerte_products');
      const products = data ? JSON.parse(data) : [];
      return products.length;
    } catch (e) {
      return 0;
    }
  };
}
